/**
 * Curated list of IANA timezones for the league lockdown picker.
 * Keep this short and ordered by likely usage for our audience.
 * Users can also free-type any IANA zone in the picker if needed.
 */

export type TimezoneOption = {
  /** IANA name, e.g. "America/Los_Angeles" */
  value: string;
  /** Friendly label shown in the dropdown */
  label: string;
};

export const COMMON_TIMEZONES: TimezoneOption[] = [
  { value: "UTC", label: "UTC" },
  { value: "America/New_York", label: "Eastern (US & Canada)" },
  { value: "America/Chicago", label: "Central (US & Canada)" },
  { value: "America/Denver", label: "Mountain (US & Canada)" },
  { value: "America/Phoenix", label: "Arizona (no DST)" },
  { value: "America/Los_Angeles", label: "Pacific (US & Canada)" },
  { value: "America/Anchorage", label: "Alaska" },
  { value: "Pacific/Honolulu", label: "Hawaii" },
  { value: "America/Toronto", label: "Toronto" },
  { value: "America/Mexico_City", label: "Mexico City" },
  { value: "America/Sao_Paulo", label: "São Paulo" },
  { value: "America/Buenos_Aires", label: "Buenos Aires" },
  { value: "Europe/London", label: "London" },
  { value: "Europe/Paris", label: "Paris / Berlin / Madrid" },
  { value: "Europe/Athens", label: "Athens / Helsinki" },
  { value: "Europe/Moscow", label: "Moscow" },
  { value: "Africa/Johannesburg", label: "Johannesburg" },
  { value: "Asia/Dubai", label: "Dubai" },
  { value: "Asia/Kolkata", label: "India" },
  { value: "Asia/Singapore", label: "Singapore" },
  { value: "Asia/Tokyo", label: "Tokyo" },
  { value: "Asia/Seoul", label: "Seoul" },
  { value: "Australia/Sydney", label: "Sydney" },
  { value: "Pacific/Auckland", label: "Auckland" },
];

/** Browser's current IANA timezone, with safe fallback to UTC. */
export function getBrowserTimezone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
  } catch {
    return "UTC";
  }
}

/**
 * Compose a UTC ISO timestamp from a wall-clock date+time interpreted in the
 * given IANA timezone.
 *
 * Example: composeZonedISO(new Date("2026-06-28"), "10:00", "America/Los_Angeles")
 *   => "2026-06-28T17:00:00.000Z"
 *
 * Works by computing the offset of the target zone at the requested wall time
 * via Intl.DateTimeFormat (no extra dependency required).
 */
export function composeZonedISO(
  date: Date,
  time: string,
  timezone: string,
): string | null {
  const [hh, mm] = time.split(":").map((v) => parseInt(v, 10));
  if (Number.isNaN(hh) || Number.isNaN(mm)) return null;

  const y = date.getFullYear();
  const m = date.getMonth() + 1;
  const d = date.getDate();

  // Start with the assumption that the wall time IS UTC. We'll then ask:
  // "what does this UTC instant look like in the target zone?" and adjust.
  const guess = Date.UTC(y, m - 1, d, hh, mm, 0, 0);
  const tzMs = getZoneOffsetMs(guess, timezone);
  // The wall time we want, expressed as ms since epoch in the target zone:
  const real = guess - tzMs;
  // DST correction: re-evaluate offset at the corrected instant.
  const tzMs2 = getZoneOffsetMs(real, timezone);
  const corrected = guess - tzMs2;

  return new Date(corrected).toISOString();
}

/**
 * Returns the offset (in ms) that the given IANA timezone has from UTC at the
 * given UTC instant. Positive for zones east of UTC.
 */
function getZoneOffsetMs(utcMs: number, timezone: string): number {
  const dtf = new Intl.DateTimeFormat("en-US", {
    timeZone: timezone,
    hourCycle: "h23",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  });
  const parts = dtf.formatToParts(new Date(utcMs));
  const get = (t: string) => parseInt(parts.find((p) => p.type === t)?.value ?? "0", 10);
  const asUTC = Date.UTC(
    get("year"),
    get("month") - 1,
    get("day"),
    get("hour"),
    get("minute"),
    get("second"),
  );
  return asUTC - utcMs;
}

/**
 * Format a UTC instant as wall-clock parts in the given timezone.
 * Useful for prefilling the date picker / time field when editing.
 */
export function getZonedParts(utcIso: string, timezone: string) {
  const dtf = new Intl.DateTimeFormat("en-CA", {
    timeZone: timezone,
    hourCycle: "h23",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
  const parts = dtf.formatToParts(new Date(utcIso));
  const get = (t: string) => parts.find((p) => p.type === t)?.value ?? "00";
  return {
    year: parseInt(get("year"), 10),
    month: parseInt(get("month"), 10),
    day: parseInt(get("day"), 10),
    hour: get("hour"),
    minute: get("minute"),
  };
}

/**
 * Format an instant for human display in a specific timezone, including a
 * short timezone abbreviation. Example: "Jun 28, 2026, 10:00 AM PDT".
 */
export function formatInTimezone(
  utcIso: string | number | Date,
  timezone: string,
  opts: Intl.DateTimeFormatOptions = {
    weekday: "short",
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
    timeZoneName: "short",
  },
): string {
  try {
    return new Intl.DateTimeFormat(undefined, { ...opts, timeZone: timezone }).format(
      new Date(utcIso),
    );
  } catch {
    return new Date(utcIso).toLocaleString();
  }
}

/** Short timezone abbreviation like "PDT" or "CET". */
export function timezoneAbbreviation(utcIso: string | number | Date, timezone: string): string {
  try {
    const parts = new Intl.DateTimeFormat("en-US", {
      timeZone: timezone,
      timeZoneName: "short",
    }).formatToParts(new Date(utcIso));
    return parts.find((p) => p.type === "timeZoneName")?.value ?? timezone;
  } catch {
    return timezone;
  }
}
